import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import VmsFooterRegionSwitcher from '@components/footer/VmsFooterRegionSwitcher';
import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import { P } from '@components/shared/Typography';
import useDesktop from '@components/shared/useDesktop';
import { BCORP_SVG, DAYE_MONOGRAM_SVG } from '@constants';
import useTranslate from '@hooks/useTranslate';
import { isInternalLink } from '@utils/language';
import CompanyAddress from './CompanyAddress';

const footerMenuItems = [
  {
    title: 'Help Centre',
    link: 'https://yourdaye.zendesk.com/hc/en-us'
  },
  {
    title: 'Daye Homepage',
    link: '/'
  },
  {
    title: 'Vitals (Our Blog)',
    link: '/vitals/'
  },
  {
    title: 'Terms & Conditions',
    link: '/dashboard/terms/'
  },
  {
    title: 'Privacy Policy',
    link: '/dashboard/privacy/'
  },
  {
    title: 'Cookie Policy',
    link: '/cookies/'
  }
];

const VMSFooter: React.FC = () => {
  const { t } = useTranslate();
  const desktop = useDesktop(1024);
  const location = useLocation();
  const [footerColor, setFooterColor] = useState(Colors.navyBlue);

  useEffect(() => {
    if (location.pathname.includes('/dashboard/')) {
      setFooterColor(Colors.navyBlue);
    }
  }, []);

  if (desktop) {
    return (
      <>
        <Container $color={footerColor}>
          <Inner>
            <DetailsWrap>
              <MonogramStyled src={DAYE_MONOGRAM_SVG} width={84} height={69} />
              <Address>
                <CompanyAddress />
              </Address>
              <img src={BCORP_SVG} alt="BCorp Certified" width="40" height="84" />
              <VmsFooterRegionSwitcher />
            </DetailsWrap>
            <LinksInnerContainer>
              {footerMenuItems.map((item, index) => (
                <LinkTranslated to={item.link} key={index} targetBlank={!isInternalLink(item.link)}>
                  {item.title}
                </LinkTranslated>
              ))}
            </LinksInnerContainer>
          </Inner>
        </Container>
        <FDA>
          <PStyled>{t('footer.vaginal-microbiome-screen-warning')}</PStyled>
        </FDA>
      </>
    );
  }

  return (
    <>
      <Container $color={footerColor}>
        <Inner>
          <div style={{ height: 30 }} />
          <LinksInnerContainer>
            {footerMenuItems.map((item, index) => (
              <LinkTranslated to={item.link} key={index} targetBlank={!isInternalLink(item.link)}>
                {item.title}
              </LinkTranslated>
            ))}
            <VmsFooterRegionSwitcher />
          </LinksInnerContainer>
          <Divider $margin="0 30px 30px" />
          <DetailsWrap>
            <div style={{ display: 'flex', gap: '20px' }}>
              <MonogramStyled src={DAYE_MONOGRAM_SVG} width={84} height={69} />
              <Address>
                <CompanyAddress />
              </Address>
            </div>
            <img src={BCORP_SVG} alt="BCorp Certified" width="40" height="84" />
          </DetailsWrap>
          <Divider $fullWidth={true} $margin="30px 0 0 0" />
          <FDA>
            <PStyled>{t('footer.vaginal-microbiome-screen-warning')}</PStyled>
          </FDA>
        </Inner>
      </Container>
    </>
  );
};

export default VMSFooter;

interface StyleProps {
  $color?: string;
  $margin?: string;
  $fullWidth?: boolean;
}

const Container = styled.div<StyleProps>`
  padding: 50px 30px 100px 30px;
  position: relative;
  background-color: ${(props) => props.$color};

  @media (max-width: 1024px) {
    padding: 30px;
  }
`;

const Inner = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const DetailsWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: 25px;

  @media (max-width: 1024px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const MonogramStyled = styled.img`
  width: 70px;
`;

const Address = styled.address`
  display: flex;
  flex-direction: column;

  p {
    font-family: 'BrownStd';
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 19.5px;
    letter-spacing: 0;
    text-align: left;
    color: ${Colors.cream};
    opacity: 0.5;

    @media (max-width: 1024px) {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;

const LinksInnerContainer = styled.div`
  position: relative;
  max-width: 440px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  width: 100%;
  justify-content: space-between;

  @media (max-width: 1024px) {
    margin-bottom: 90px;
  }

  a {
    font-family: 'BrownStd';
    font-size: 13px;
    font-weight: 400;
    line-height: 23.4px;
    letter-spacing: 0;
    text-align: left;
    color: ${Colors.babyBlue};
    white-space: nowrap;
    width: 50%;

    @media (max-width: 1024px) {
      font-size: 14px;
      font-weight: 400;
      line-height: 28px;
    }
  }
`;

const FDA = styled.div`
  background: ${Colors.navyBlue};
  border-top: 1px solid rgba(235, 241, 245, 0.2);
  width: 100%;
  padding: 20px 30px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    border-top: 0;
  }
`;

const PStyled = styled(P)`
  color: ${Colors.babyBlue}80;
  max-width: 1200px;
  width: fit-content;
  margin: 0 auto;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  text-align: center;
`;

const Divider = styled.div<StyleProps>`
  width: ${(props) => (props.$fullWidth ? 'calc(100% + 60px)' : '100%')};
  height: 1px;
  background: ${Colors.babyBlue}33;
  margin: ${(props) => props.$margin};
`;
